import { REJECT_REASON_LABELS, type CaptureJob, type CaptureRejection, type CaptureStatus } from '@/lib/api/types';

/** The first non-blank line of a job's instructions, which is what a job
 *  card shows: managers lead with the site or the task. */
export function firstLine(instructions: string): string {
  return (
    instructions
      .split(/\r?\n/)
      .map((line) => line.trim())
      .find((line) => line !== '') ?? ''
  );
}

export function captureCount(job: Pick<CaptureJob, 'counts'>): number {
  return job.counts?.total ?? 0;
}

export function captureCountLabel(count: number): string {
  if (count === 0) return 'No captures yet';
  return `${count} ${count === 1 ? 'capture' : 'captures'}`;
}

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'brand';

/**
 * The words an operator reads for each capture status on the job view. The
 * pipeline's own stage names (PREFLIGHT, VALIDATING, SCORED) mean nothing
 * at the shelf, so every stage before a person has looked at it reads as
 * "Reading", and each finished state names what happened.
 */
export const CAPTURE_STATUS_TEXT: Record<CaptureStatus, { label: string; tone: Tone }> = {
  QUEUED: { label: 'Uploading', tone: 'neutral' },
  RECEIVED: { label: 'Reading', tone: 'neutral' },
  PREFLIGHT: { label: 'Reading', tone: 'neutral' },
  EXTRACTING: { label: 'Reading', tone: 'neutral' },
  VALIDATING: { label: 'Reading', tone: 'neutral' },
  SCORED: { label: 'Ready to check', tone: 'brand' },
  AUTO_APPROVED: { label: 'Approved', tone: 'success' },
  IN_REVIEW: { label: 'In review', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  RECAPTURE_REQUESTED: { label: 'Photograph again', tone: 'warning' },
  STAGED: { label: 'Approved', tone: 'success' },
  SUBMITTED: { label: 'Sent on', tone: 'success' },
  QUARANTINED: { label: 'Could not be read', tone: 'danger' },
  REMOVED: { label: 'Removed', tone: 'neutral' },
};

/**
 * The reason a capture was rejected, as its catalogue label (INTERFACES.md
 * section 3), with "Auto" when an auto-reject rule rejected it rather than
 * a person. A code this build does not know reads as Other.
 */
export function rejectionText(rejection: Pick<CaptureRejection, 'code' | 'auto'>): string {
  const label = REJECT_REASON_LABELS[rejection.code] ?? REJECT_REASON_LABELS.OTHER;
  return rejection.auto ? `${label} · Auto` : label;
}

/**
 * A capture's status for a list row. A rejected capture names its reason
 * beside the status, so the operator sees why without opening it; every
 * other status reads as `CAPTURE_STATUS_TEXT` has it.
 */
export function captureStatusText(
  status: CaptureStatus,
  rejection?: Pick<CaptureRejection, 'code' | 'auto'>,
): { label: string; tone: Tone } {
  const base = CAPTURE_STATUS_TEXT[status] ?? { label: status, tone: 'neutral' as const };
  if (status === 'REJECTED' && rejection) {
    return { label: `${base.label} · ${rejectionText(rejection)}`, tone: base.tone };
  }
  return base;
}
